import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, ArrowRight, Clock, Users, CheckCircle2, Target, 
  BookOpen, Briefcase, GraduationCap, Award, Building2, DollarSign
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getProgramBySlug, PROGRAM_CATEGORIES } from "@/data/programCatalog";

/**
 * ProgramDetails - Reusable program details page template
 * Renders program information based on URL slug from programCatalog.js
 */
export default function ProgramDetails() {
  const { programSlug } = useParams();
  const program = getProgramBySlug(programSlug);

  // Redirect if program not found
  if (!program) {
    return <Navigate to="/programs" replace />;
  }

  const category = PROGRAM_CATEGORIES[program.category];
  const isEMP = program.category === "executive-professional-masters";

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid={`program-${program.slug}`}>
      {/* Hero Section */}
      <section className="bg-[#1E3A8A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link 
              to="/programs" 
              className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Programs
            </Link>
            
            <div className="flex flex-wrap gap-2 mb-4">
              <Badge 
                className="text-white border-white/30"
                style={{ backgroundColor: `${category.color}80` }}
              >
                {category.title}
              </Badge>
            </div>
            
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
              {program.title}
            </h1>
            
            <p className="text-lg text-white/80 leading-relaxed mb-8 max-w-3xl">
              {program.shortDescription}
            </p>
            
            <div className="flex flex-wrap gap-6 mb-8 text-sm">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-white/70" />
                <span>{program.duration}</span>
              </div>
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-white/70" />
                <span>{program.format}</span>
              </div>
              {program.tuition && (
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-white/70" />
                  <span>{program.tuition}</span>
                </div>
              )}
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/programs/apply">
                <Button size="lg" className="bg-white text-[#1E3A8A] hover:bg-white/90 h-12 px-8">
                  Apply Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="h-12 px-8 border-white/30 text-white hover:bg-white/10">
                  Contact Admissions
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Program Overview */}
      <section className="py-12 lg:py-16 bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-6" style={{ color: '#991B1B' }}>
              Program Overview
            </h2>
            <p className="text-lg text-[#1E3A8A] leading-relaxed max-w-4xl">
              {program.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Program Vision */}
      {program.vision && (
        <section className="py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Card className="border-[#E5E7EB] bg-gradient-to-br from-[#1E3A8A]/5 to-transparent">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#1E3A8A]/10 flex items-center justify-center flex-shrink-0">
                      <Target className="w-6 h-6 text-[#1E3A8A]" />
                    </div>
                    <div>
                      <h2 className="font-heading text-2xl font-bold mb-4" style={{ color: '#991B1B' }}>
                        Program Vision
                      </h2>
                      <p className="text-lg text-[#1E3A8A] leading-relaxed">
                        {program.vision}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>
      )}

      {/* Program Objectives */}
      {program.objectives && program.objectives.length > 0 && (
        <section className="py-12 lg:py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-8" style={{ color: '#991B1B' }}>
                Program Objectives
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {program.objectives.map((objective, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 rounded-lg bg-[#F8FAFC] border border-[#E5E7EB]">
                    <CheckCircle2 className="w-5 h-5 text-[#1E3A8A] flex-shrink-0 mt-0.5" />
                    <span className="text-[#1E3A8A]">{objective}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Program Orientation */}
      {program.orientation && program.orientation.length > 0 && (
        <section className="py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-6" style={{ color: '#991B1B' }}>
                Program Orientation
              </h2>
              <div className="flex flex-wrap gap-3">
                {program.orientation.map((item, index) => (
                  <Badge 
                    key={index}
                    variant="outline"
                    className="py-2 px-4 text-sm bg-[#1E3A8A]/5 text-[#1E3A8A] border-[#1E3A8A]/20"
                  >
                    {item}
                  </Badge>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Program Structure (for EMP programs) */}
      {program.structure && program.structure.length > 0 && (
        <section className="py-12 lg:py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-6" style={{ color: '#991B1B' }}>
                Program Structure
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {program.structure.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 rounded-lg border border-[#E5E7EB] bg-[#F8FAFC]">
                    <BookOpen className="w-5 h-5 text-[#1E3A8A]" />
                    <span className="text-[#1E3A8A]">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Program Curriculum (for EMP programs) */}
      {isEMP && program.curriculum && program.curriculum.length > 0 && (
        <section className="py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-8" style={{ color: '#991B1B' }}>
                Program Curriculum
              </h2>
              <div className="space-y-4">
                {program.curriculum.map((module, index) => (
                  <Card key={index} className="border-[#E5E7EB] bg-white hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div 
                          className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${category.color}15` }}
                        >
                          <span className="font-mono font-bold text-[#1E3A8A]">{index + 1}</span>
                        </div>
                        <div>
                          <p className="text-xs text-[#1E3A8A]/50 uppercase tracking-wider mb-1">
                            {module.module}
                          </p>
                          <h3 className="font-heading font-semibold text-lg mb-1" style={{ color: '#991B1B' }}>
                            {module.title}
                          </h3>
                          <p className="text-[#1E3A8A] text-sm">{module.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Career Pathways */}
      {program.careerPathways && program.careerPathways.length > 0 && (
        <section className="py-12 lg:py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-6" style={{ color: '#991B1B' }}>
                Career Pathways
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {program.careerPathways.map((pathway, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 rounded-lg border border-[#E5E7EB] bg-[#F8FAFC]">
                    <Briefcase className="w-5 h-5 text-[#991B1B]" />
                    <span className="text-[#1E3A8A] font-medium">{pathway}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[#991B1B] to-[#7F1D1D] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <GraduationCap className="w-12 h-12 mx-auto mb-6 text-white/80" />
            <h2 className="font-heading text-3xl font-bold mb-4">
              Ready to Begin Your Journey?
            </h2>
            <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
              Take the next step in your professional development. Apply now or contact our admissions team to learn more about {program.title}.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/programs/apply">
                <Button size="lg" className="bg-white text-[#991B1B] hover:bg-white/90 h-12 px-8">
                  Apply to Program
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="h-12 px-8 border-white/30 text-white hover:bg-white/10">
                  Contact Admissions
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
